<template>
    <div>
        <progress-header />

        <search-form
            :start-places="startPlaces"
            :end-places="endPlaces"
            :initial-parameters="initialParameters"

            :is-start-places-loading="isStartPlacesLoading"
            :is-end-places-loading="isEndPlacesLoading"

            @findStartPlaces="findStartPlaces"
            @findEndPlaces="findEndPlaces"

            @startPlacePicked="onStartPlacePicked"
            @endPlacePicked="onEndPlacePicked"

            @swapPlaces="swapPlaces"

            @search="search"
        />

        <search-tips type="berbus" />
    </div>
</template>

<script>
    import {
        BERBUS_PLACE_VENDOR_GMAPS,
        BERBUS_PLACE_VENDOR_LOCAL,
        PASSENGER_TYPE_ADULT,
        PASSENGER_TYPE_CHILD_AGE_3,
    } from '@/constants';
    import ProgressHeader from './components/ProgressHeader';
    import SearchForm from './components/SearchForm';
    import {trackGA} from '@utilities/tracking';
    import SearchTips from '@components/SearchTips';
    import backofficeRepository from '@repositories/BackofficeRepository';

    export default {
        components: {
            ProgressHeader,
            SearchForm,
            SearchTips,
        },

        data() {
            return {
                trackingName: 'BERBUS',

                isStartPlacesLoading: false,
                isEndPlacesLoading: false,

                isStartPlacesSearchable: true,
                isEndPlacesSearchable: false,

                startPlaces: [],
                endPlaces: [],

                startPlace: null,
                endPlace: null,

                //current user location got from the browser, defaults to Szczecin
                geoCurrentPosition: {coords: {latitude: 53.42, longitude: 14.56}},
            };
        },

        computed: {
            initialParameters() {
                return {};
            },
        },

        created () {
            //ask user for location
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(position => this.geoCurrentPosition = position);
            }

            trackGA('view_item', {
                'items': [
                    {
                        id: window.location.pathname,
                        name: this.trackingName,
                        quantity: 1,
                    },
                ],
            });
            trackGA('checkout_progress', {
                checkout_step: 1,
                checkout_option: window.location.pathname,
            });
        },

        methods: {
            search(params) {
                return this.$store.dispatch('berbus/reset').then(() => {
                    const passengersAdult = params.passengers.find((passenger) => {
                        return passenger.type === PASSENGER_TYPE_ADULT;
                    });

                    const passengersChildAge3 = params.passengers.find((passenger) => {
                        return passenger.type === PASSENGER_TYPE_CHILD_AGE_3;
                    });

                    return this.$router.push({
                        name: 'berbus.address',
                        query: {
                            start_address_id: params.start_place.id,
                            start_address: params.start_place.start_place,
                            start_vendor: params.start_place.vendor,
                            end_address_id: params.end_place.id,
                            end_address: params.end_place.end_place,
                            end_vendor: params.end_place.vendor,
                            departure_date: params.departure_date,
                            departure_time: params.departure_time,
                            passengers_adult: passengersAdult.quantity,
                            passengers_child_age_3: passengersChildAge3.quantity,
                        },
                    });
                });
            },

            findStartPlaces(query) {
                this.startPlaces = [];

                if (query.length <= 2) {
                    return;
                }

                this.isStartPlacesLoading = true;

                const placesParams = {
                    start_place: query,
                };

                if (this.endPlace && this.endPlace.vendor === BERBUS_PLACE_VENDOR_LOCAL) {
                    placesParams.end_place = this.endPlace.end_place;
                }

                backofficeRepository.searchMapAutocomplete(query, this.geoCurrentPosition.coords.latitude, this.geoCurrentPosition.coords.longitude).then((places) => {
                    this.startPlaces = places.map((place) => {
                        const isAirport = place.placeId.indexOf('_AIRPORT') > -1;
                        return {
                            id: place.placeId,
                            start_place: place.description,
                            start_is_airport: isAirport,
                            vendor: place.types.includes('geocode')?BERBUS_PLACE_VENDOR_GMAPS:BERBUS_PLACE_VENDOR_LOCAL,
                            hours: null,
                        };
                    });
                }).finally(() => {
                    this.isStartPlacesLoading = false;
                });
            },

            findEndPlaces(query) {
                this.endPlaces = [];

                if (query.length <= 2) {
                    return;
                }

                this.isEndPlacesLoading = true;

                const placesParams = {
                    end_place: query,
                };

                if (this.startPlace && this.startPlace.vendor === BERBUS_PLACE_VENDOR_LOCAL) {
                    placesParams.start_place = this.startPlace.start_place;
                }

                backofficeRepository.searchMapAutocomplete(query, this.geoCurrentPosition?this.geoCurrentPosition.coords.latitude:null, this.geoCurrentPosition?this.geoCurrentPosition.coords.longitude:null).then((places) => {
                    this.endPlaces = places.map((place) => {
                        const isAirport = place.placeId.indexOf('_AIRPORT') > -1;
                        return {
                            id: place.placeId,
                            end_place: place.description,
                            end_is_airport: isAirport,
                            vendor: place.types.includes('geocode')?BERBUS_PLACE_VENDOR_GMAPS:BERBUS_PLACE_VENDOR_LOCAL,
                            hours: null,
                        };
                    });
                }).finally(() => {
                    this.isEndPlacesLoading = false;
                });
            },

            onStartPlacePicked(place) {
                this.startPlace = place;
            },

            onEndPlacePicked(place) {
                this.endPlace = place;
            },

            swapPlaces() {
                this.isStartPlacesSearchable = !this.isStartPlacesSearchable;
                this.isEndPlacesSearchable = !this.isEndPlacesSearchable;

                const startPlacesDump = JSON.parse(JSON.stringify(this.startPlaces));
                const endPlacesDump = JSON.parse(JSON.stringify(this.endPlaces));

                this.startPlaces = endPlacesDump.map((place) => {
                    return {
                        id: place.id,
                        start_place: place.end_place,
                        start_is_airport: place.end_is_airport,
                        vendor: place.vendor,
                    };
                });

                this.endPlaces = startPlacesDump.map((place) => {
                    return {
                        id: place.id,
                        end_place: place.start_place,
                        end_is_airport: place.start_is_airport,
                        vendor: place.vendor,
                    };
                });
            },
        },
    };
</script>
